import React from 'react';

import { TableColumns } from '@models/column';
import { JoinCondition } from '@models/feature-descriptor';

import { JoinConditionDraft } from '../../models';
import {
  FeatureDescriptorEditorActions,
  useFeatureDescriptorEditorContext,
} from '../../context';
import { JoinConditionSelectionType } from './JoinConditionTypeSelection';
import { EditorWrapper } from '../EditorWrapper';
import {
  ColumnEditor,
  DraftEditorProps,
  StringEditor,
  NumberEditor,
} from '../field-widgets';

const joinConditionEditorTemplate: Record<
  JoinCondition.Type,
  (props: DraftEditorProps<JoinConditionDraft>) => JSX.Element[]
> = {
  [JoinCondition.Type.Geo]: () => [],
  [JoinCondition.Type.Same]: props => {
    const [state] = useFeatureDescriptorEditorContext();

    return [
      <ColumnEditor
        label="target_side_column"
        columns={props.tableColumns[state.join.target_table] ?? []}
        {...props}
      />,
      <ColumnEditor
        label="source_side_column"
        columns={props.tableColumns[state.join.source_table] ?? []}
        {...props}
      />,
    ];
  },
  [JoinCondition.Type.Time]: props => {
    const [state] = useFeatureDescriptorEditorContext();

    return [
      <ColumnEditor
        label="target_side_column"
        columns={props.tableColumns[state.join.target_table] ?? []}
        {...props}
      />,
      <ColumnEditor
        label="source_side_column"
        columns={props.tableColumns[state.join.source_table] ?? []}
        {...props}
      />,
      <StringEditor label="from_value" {...props} />,
      <StringEditor label="to_value" {...props} />,
    ];
  },
  [JoinCondition.Type.PeriodicTime]: props => {
    const [state] = useFeatureDescriptorEditorContext();
    return [
      <ColumnEditor
        label="target_side_column"
        columns={props.tableColumns[state.join.target_table] ?? []}
        {...props}
      />,
      <ColumnEditor
        label="source_side_column"
        columns={props.tableColumns[state.join.source_table] ?? []}
        {...props}
      />,
      <StringEditor label="periodic_unit" {...props} />,
      <NumberEditor label="num_cycles" {...props} />,
    ];
  },
};

const InlineJoinConditionEditor = (props: {
  tableColumns: TableColumns;
  joinCondition: Partial<JoinConditionDraft>;
  onJoinConditionChange: (params: Partial<JoinConditionDraft>) => void;
}) => {
  const templates = props.joinCondition.type
    ? joinConditionEditorTemplate[props.joinCondition.type]({
        tableColumns: props.tableColumns,
        draft: props.joinCondition,
        onDraftChange: props.onJoinConditionChange,
      })
    : [];

  return (
    <EditorWrapper
      templates={templates}
      selectorWidth={130}
      selectedType={props.joinCondition.type}
    >
      <JoinConditionSelectionType
        value={props.joinCondition.type}
        onValueChange={type =>
          props.onJoinConditionChange({
            type,
            id: props.joinCondition.id,
          })
        }
      />
    </EditorWrapper>
  );
};

export const JoinConditionEditor = (props: {
  conditionId: number;
  tableColumns: TableColumns;
}) => {
  const [state, dispatch] = useFeatureDescriptorEditorContext();

  const joinCondition: JoinConditionDraft = state.join.join_conditions.find(
    ({ id }: JoinConditionDraft) => id === props.conditionId
  ) ?? { id: props.conditionId };

  const setJoinConditionDraft = (params: Partial<JoinConditionDraft>) => {
    const join = {
      ...state.join,
      join_conditions: state.join.join_conditions.map(
        (draft: Partial<JoinConditionDraft>) =>
          draft.id === props.conditionId ? params : draft
      ),
    };
    dispatch(
      FeatureDescriptorEditorActions.setDraftField({
        field: 'join',
        value: join,
      })
    );
  };

  return (
    <InlineJoinConditionEditor
      joinCondition={joinCondition}
      tableColumns={props.tableColumns}
      onJoinConditionChange={setJoinConditionDraft}
    />
  );
};
